// Public, no-auth traceability viewer. Reachable at
// /share/ot/:code — the recipient gets the URL by email and enters
// the 4-digit PIN that arrived in the same email to unlock the data.
//
// Reuses the same tab components as the authenticated viewer so the
// public and internal experiences stay in lock-step. Wrapped in a
// branded shell (Abeya logo + "Powered by Abeya" footer) so the buyer
// always sees they're on Abeya's surface, not the exporter's portal.
// Producer names are masked server-side; the recipient sees opaque
// "Producer #<id>" labels rather than farmer PII.

import { useState } from "react"
import {
  AlertTriangle,
  KeyRound,
  LayoutDashboard,
  Loader2,
  MapPin,
  Receipt,
  ShieldCheck,
  Ship,
  Users,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { useMutation, useQuery } from "@tanstack/react-query"
import { useParams } from "react-router-dom"

import { LanguageToggle } from "@/components/language-toggle"
import { ThemeToggle } from "@/components/theme-toggle"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  accessPublicShare,
  getPublicSharePreview,
} from "@/features/transit/api/transit.api"
import { TraceabilityDeliveriesTab } from "@/features/transit/components/traceability/deliveries-tab"
import { TraceabilityTransactionsTabPublic } from "@/features/transit/components/traceability/transactions-tab-public"
import { TraceabilityParcelsTabPublic } from "@/features/transit/components/traceability/parcels-tab-public"
import { TraceabilityProducersTab } from "@/features/transit/components/traceability/producers-tab"
import { TraceabilitySummaryTab } from "@/features/transit/components/traceability/summary-tab"
import { resolveApiError } from "@/lib/api/error-i18n"
import type { OT, PublicShareData } from "@/features/transit/api/types"

// Logo lives in /public; reference by absolute path so the Vite build
// doesn't have to inline-bundle it.
const ABEYA_LOGO = "/abeya-indigo-logo.svg"

export function PublicSharePage() {
  const { code = "" } = useParams<{ code: string }>()
  const { t } = useTranslation("transit")

  const [pin, setPin] = useState("")
  const [activeTab, setActiveTab] = useState("summary")

  const previewQuery = useQuery({
    queryKey: ["public-share", "preview", code],
    queryFn: () => getPublicSharePreview(code),
    enabled: code.length > 0,
    retry: false,
  })

  const accessMutation = useMutation({
    mutationFn: (value: string) => accessPublicShare(code, value),
  })
  const data: PublicShareData | undefined = accessMutation.data

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    const value = pin.trim()
    if (value.length !== 4 || accessMutation.isPending) return
    accessMutation.mutate(value)
  }

  const previewLoading = previewQuery.isLoading
  const previewError = previewQuery.isError
    ? resolveApiError(previewQuery.error, t)
    : null
  const preview = previewQuery.data ?? null
  const accessError = accessMutation.isError
    ? resolveApiError(accessMutation.error, t)
    : null
  const accessLoading = accessMutation.isPending

  if (previewLoading) {
    return (
      <PublicShell>
        <Centered>
          <p className="text-sm text-muted-foreground">
            {t("trace.publicShare.loading")}
          </p>
        </Centered>
      </PublicShell>
    )
  }

  if (previewError || !preview) {
    return (
      <PublicShell>
        <Centered>
          <Card className="flex max-w-md flex-col items-center gap-3 p-6 text-center">
            <AlertTriangle className="size-6 text-amber-500" />
            <h1 className="text-lg font-semibold tracking-tight text-foreground">
              {t("trace.publicShare.unavailableTitle")}
            </h1>
            <p className="text-sm text-muted-foreground">
              {previewError ?? t("trace.publicShare.unavailableHint")}
            </p>
          </Card>
        </Centered>
      </PublicShell>
    )
  }

  if (!data) {
    return (
      <PublicShell>
        <Centered>
          <Card className="flex max-w-md flex-col gap-4 p-6">
            <div className="flex items-center gap-2">
              <ShieldCheck className="size-5 text-emerald-600" />
              <h1 className="text-lg font-semibold tracking-tight text-foreground">
                {t("trace.publicShare.title")}
              </h1>
            </div>
            <SharerSummary preview={preview} />
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="share-pin"
                  className="text-xs font-medium text-foreground"
                >
                  {t("trace.publicShare.pinLabel")}
                </label>
                <Input
                  id="share-pin"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={4}
                  autoComplete="off"
                  value={pin}
                  onChange={(e) => {
                    // Strip non-digits so paste from a copy with spaces
                    // still works.
                    setPin(e.target.value.replace(/\D/g, "").slice(0, 4))
                  }}
                  placeholder="••••"
                  className="text-center text-lg tracking-[0.5em] font-mono"
                />
                <p className="text-[11px] text-muted-foreground">
                  {t("trace.publicShare.pinHint")}
                </p>
              </div>
              {accessError ? (
                <p className="rounded-md border border-red-200 bg-red-50/60 px-3 py-2 text-xs text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                  {accessError}
                </p>
              ) : null}
              <Button
                type="submit"
                disabled={pin.trim().length !== 4 || accessLoading}
              >
                {accessLoading ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <KeyRound className="size-4" />
                )}
                {t("trace.publicShare.submit")}
              </Button>
            </form>
          </Card>
        </Centered>
      </PublicShell>
    )
  }

  // Authenticated view — render the same tabs the internal page uses.
  const otShim: OT = synthesizeOTFromPreview(data.ot)
  const exporterName =
    data.ot.exporterName ?? preview.exporterName ?? ""

  return (
    <PublicShell>
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-5 px-6 py-6">
        <header className="flex flex-col gap-2">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-xl font-semibold tracking-tight text-foreground">
                {t("trace.publicShare.heading")}
              </h1>
              <p className="text-sm text-muted-foreground">
                {data.ot.otNumber} · {data.ot.clientName}
                {data.ot.vessel || data.ot.voyage ? (
                  <>
                    {" · "}
                    {[data.ot.vessel, data.ot.voyage]
                      .filter(Boolean)
                      .join(" · ")}
                  </>
                ) : null}
              </p>
            </div>
            {exporterName ? (
              <div className="flex items-center gap-2 rounded-full border bg-muted/30 px-3 py-1 text-xs text-muted-foreground">
                <ShieldCheck className="size-3.5 text-emerald-600" />
                <span>
                  {t("trace.publicShare.sharedByExporter", {
                    exporter: exporterName,
                  })}
                </span>
              </div>
            ) : null}
          </div>
        </header>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="summary">
              <LayoutDashboard className="size-4" />
              {t("trace.tabs.summary")}
            </TabsTrigger>
            <TabsTrigger value="producers">
              <Users className="size-4" />
              {t("trace.tabs.producers", {
                count: data.traceability.summary.producers.total,
              })}
            </TabsTrigger>
            <TabsTrigger value="parcels">
              <MapPin className="size-4" />
              {t("trace.tabs.parcels", {
                count: data.traceability.summary.parcels.total,
              })}
            </TabsTrigger>
            <TabsTrigger value="deliveries">
              <Ship className="size-4" />
              {t("trace.tabs.deliveries", {
                count: data.traceability.summary.deliveries.total,
              })}
            </TabsTrigger>
            <TabsTrigger value="transactions">
              <Receipt className="size-4" />
              {t("trace.tabs.transactions", {
                count: data.traceability.summary.purchases.total,
              })}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="summary" className="pt-4">
            <TraceabilitySummaryTab
              ot={otShim}
              traceability={data.traceability}
            />
          </TabsContent>
          <TabsContent value="producers" className="pt-4">
            <TraceabilityProducersTab traceability={data.traceability} />
          </TabsContent>
          <TabsContent value="parcels" className="pt-4">
            <TraceabilityParcelsTabPublic
              traceability={data.traceability}
              parcelGeo={data.parcelGeo}
            />
          </TabsContent>
          <TabsContent value="deliveries" className="pt-4">
            <TraceabilityDeliveriesTab traceability={data.traceability} />
          </TabsContent>
          <TabsContent value="transactions" className="pt-4">
            <TraceabilityTransactionsTabPublic
              traceability={data.traceability}
            />
          </TabsContent>
        </Tabs>
      </div>
    </PublicShell>
  )
}

function SharerSummary({
  preview,
}: {
  preview: import("@/features/transit/api/types").PublicSharePreview
}) {
  const { t } = useTranslation("transit")
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {t("trace.publicShare.shipmentLabel")}
      </div>
      <div className="mt-1 text-base font-semibold text-foreground">
        {preview.otNumber}
      </div>
      <p className="text-sm text-muted-foreground">
        {preview.clientName}
        {preview.vessel || preview.voyage ? (
          <>
            {" · "}
            {[preview.vessel, preview.voyage].filter(Boolean).join(" · ")}
          </>
        ) : null}
      </p>
      {preview.exporterName ? (
        <p className="mt-2 text-xs text-muted-foreground">
          {t("trace.publicShare.sharedByExporter", {
            exporter: preview.exporterName,
          })}
          {preview.sharedBy ? <> · {preview.sharedBy}</> : null}
        </p>
      ) : preview.sharedBy ? (
        <p className="mt-2 text-xs text-muted-foreground">
          {t("trace.publicShare.sharedBy", { name: preview.sharedBy })}
        </p>
      ) : null}
    </div>
  )
}

function PublicShell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation("transit")
  return (
    <div className="flex min-h-screen flex-col bg-muted/20">
      <header className="border-b bg-background">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-3 px-6 py-3">
          <a
            href="https://abeya.co"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2"
          >
            <img src={ABEYA_LOGO} alt="Abeya" className="h-7" />
          </a>
          <div className="flex items-center gap-1">
            <span className="hidden text-[10px] font-medium uppercase tracking-wide text-muted-foreground sm:inline">
              {t("trace.publicShare.shellTagline")}
            </span>
            <LanguageToggle />
            <ThemeToggle />
          </div>
        </div>
      </header>
      <main className="flex-1">{children}</main>
      <footer className="border-t bg-background">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-center gap-1 px-6 py-3 text-[11px] text-muted-foreground">
          <span>{t("trace.publicShare.poweredBy")}</span>
          <a
            href="https://abeya.co"
            target="_blank"
            rel="noreferrer"
            className="font-medium hover:text-foreground hover:underline"
          >
            Abeya
          </a>
        </div>
      </footer>
    </div>
  )
}

// synthesizeOTFromPreview — minimal stub for the SummaryTab's `OT`
// dependency. We don't expose internal ids on the public page; the tab
// only reads label / fields the preview already carries.
function synthesizeOTFromPreview(preview: PublicShareData["ot"]): OT {
  return {
    id: 0,
    otNumber: preview.otNumber,
    label: preview.otNumber,
    contractId: 0,
    contractNumber: undefined,
    clientName: preview.clientName,
    certifications: null,
    vessel: preview.vessel ?? null,
    voyage: preview.voyage ?? null,
    pol: null,
    pod: null,
    etd: null,
    fumigationAllowed: false,
    stage: "TERMINER",
    unlockedStages: ["TERMINER"],
    archived: true,
  } as OT
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-1 items-center justify-center p-6">
      {children}
    </div>
  )
}
